'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { DEFAULT_COLUMNS, DEFAULT_OVERSCAN_ROWS } from './constants'
import type { VirtualizedListProps } from './types'

/**
 * `VirtualizedList` — windowing genérico por fila (FR-013): solo se montan en
 * el DOM las filas dentro del viewport + `overscanRows`, con independencia de
 * cuántos ítems traiga `items` (hasta 600-700). Sigue el scroll del propio
 * documento (no crea un contenedor con scroll interno) para que el listado se
 * comporte como el resto de la página. Con `columns > 1` cada fila es un grid
 * CSS de esa anchura — se sigue virtualizando por fila, no por ítem individual.
 *
 * El alto de cada fila se **mide de verdad** con `ResizeObserver` (no solo se
 * estima): `estimatedRowHeight` solo se usa como valor de partida para filas
 * aún no medidas — una fila cuyo contenido real es más alto que la estimación
 * ya no desplaza/solapa la siguiente. La medición se guarda en estado
 * (`measuredHeights`), nunca en un ref leído durante el render: tanto
 * `containerRef` como la creación del `ResizeObserver` de cada fila solo se
 * tocan dentro de efectos/callbacks, nunca en el cuerpo del componente
 * (incompatible con el compilador de React del proyecto).
 */
export function VirtualizedList<TItem>({
  items,
  itemKey,
  renderItem,
  estimatedRowHeight,
  columns = DEFAULT_COLUMNS,
  columnGap,
  rowGap = 0,
  overscanRows = DEFAULT_OVERSCAN_ROWS,
  className,
  rowClassName,
  insertAfterRow,
  insertContent,
  insertEstimatedHeight = 0,
}: VirtualizedListProps<TItem>) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [range, setRange] = useState({ start: 0, end: 0 })
  const [measuredHeights, setMeasuredHeights] = useState<Record<number, number>>({})
  const [insertMeasuredHeight, setInsertMeasuredHeight] = useState<number | null>(null)

  // Reinicia las medidas cuando cambia la composición de filas (p. ej. cambia
  // el número de columnas al cruzar un breakpoint): índices de fila antiguos
  // ya no corresponden a las mismas filas. Ajuste de estado durante el propio
  // render (comparando con el valor anterior), no en un efecto — evita un
  // ciclo de render en cascada innecesario (react-hooks/set-state-in-effect).
  const [prevColumns, setPrevColumns] = useState(columns)
  let measuredHeightsForRender = measuredHeights
  if (columns !== prevColumns) {
    setPrevColumns(columns)
    measuredHeightsForRender = {}
    setMeasuredHeights(measuredHeightsForRender)
  }

  const rowCount = Math.ceil(items.length / columns)
  // Solo hay hueco real para el bloque insertado si queda al menos una fila
  // de ítems DESPUÉS de él — con menos filas de las necesarias no se pinta.
  // Cada breakpoint decide por su cuenta: `rowCount` ya depende de `columns`,
  // que cambia con el viewport actual (`useResponsiveColumns`).
  const showInsert = insertContent !== undefined && insertAfterRow !== undefined && rowCount > insertAfterRow + 1
  const insertHeight = insertMeasuredHeight ?? insertEstimatedHeight

  const { offsets, insertTop } = useMemo(() => {
    const result = new Array<number>(rowCount + 1).fill(0)
    let top = 0
    for (let index = 0; index < rowCount; index += 1) {
      const rowHeight = measuredHeightsForRender[index] ?? estimatedRowHeight
      // `rowGap` separa cada fila de la siguiente (aparte del alto real de la
      // fila): sin él, filas consecutivas quedan a tope una con otra y el CTA
      // de una fila toca la imagen de la siguiente.
      result[index + 1] = result[index] + rowHeight + rowGap
      if (showInsert && index === insertAfterRow) {
        top = result[index + 1]
        result[index + 1] += insertHeight + rowGap
      }
    }
    return { offsets: result, insertTop: top }
  }, [rowCount, estimatedRowHeight, measuredHeightsForRender, rowGap, showInsert, insertAfterRow, insertHeight])

  const totalHeight = offsets[rowCount] ?? 0

  const computeRange = useCallback(() => {
    const container = containerRef.current
    if (!container || rowCount === 0) {
      setRange({ start: 0, end: 0 })
      return
    }

    const containerTop = container.getBoundingClientRect().top + window.scrollY
    const viewportTop = window.scrollY
    const viewportBottom = viewportTop + window.innerHeight

    const relativeTop = Math.max(0, viewportTop - containerTop)
    const relativeBottom = Math.max(0, viewportBottom - containerTop)

    let start = 0
    while (start < rowCount && offsets[start + 1] < relativeTop) start += 1
    let end = start
    while (end < rowCount && offsets[end] < relativeBottom) end += 1

    setRange({
      start: Math.max(0, start - overscanRows),
      end: Math.min(rowCount, end + overscanRows),
    })
  }, [offsets, overscanRows, rowCount])

  useEffect(() => {
    computeRange()
  }, [computeRange])

  useEffect(() => {
    let ticking = false
    const onScrollOrResize = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        computeRange()
        ticking = false
      })
    }
    window.addEventListener('scroll', onScrollOrResize, { passive: true })
    window.addEventListener('resize', onScrollOrResize)
    return () => {
      window.removeEventListener('scroll', onScrollOrResize)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [computeRange])

  const measureRow = useCallback(
    (rowIndex: number) => (node: HTMLDivElement | null) => {
      if (!node) return undefined
      const observer = new ResizeObserver(([entry]) => {
        const measured = Math.round(entry.contentRect.height)
        if (measured <= 0) return
        setMeasuredHeights((current) =>
          current[rowIndex] === measured ? current : { ...current, [rowIndex]: measured },
        )
      })
      observer.observe(node)
      return () => observer.disconnect()
    },
    [],
  )

  // Igual criterio que `measureRow`: el bloque insertado no tiene un alto fijo
  // (depende de su propio contenido/breakpoint), así que se mide de verdad en
  // vez de confiar solo en `insertEstimatedHeight`.
  const measureInsert = useCallback((node: HTMLDivElement | null) => {
    if (!node) return undefined
    const observer = new ResizeObserver(([entry]) => {
      const measured = Math.round(entry.contentRect.height)
      if (measured <= 0) return
      setInsertMeasuredHeight((current) => (current === measured ? current : measured))
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const visibleRows = []
  for (let row = range.start; row < range.end; row += 1) {
    const start = row * columns
    const rowItems = items.slice(start, start + columns)
    if (rowItems.length === 0) continue
    visibleRows.push(
      <div
        key={itemKey(rowItems[0])}
        ref={measureRow(row)}
        className={rowClassName}
        style={{
          position: 'absolute',
          top: offsets[row],
          left: 0,
          right: 0,
          ...(columns > 1
            ? { display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: columnGap }
            : undefined),
        }}
      >
        {rowItems.map((item, columnIndex) => (
          // `minWidth: 0` evita que el contenido intrínseco de un ítem (texto
          // sin wrap, etc.) fuerce esta pista del grid a crecer más que el
          // resto de la fila, lo que desalinearía el ancho — y con él,
          // cualquier alto derivado de un aspect-ratio — entre columnas.
          <div key={itemKey(item)} style={columns > 1 ? { minWidth: 0 } : undefined}>
            {/* `start` es el índice absoluto del primer ítem de esta fila
                (`row * columns`), así que `start + columnIndex` es la posición
                real en `items`, no la del DOM montado. */}
            {renderItem(item, start + columnIndex)}
          </div>
        ))}
      </div>,
    )
  }

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ position: 'relative', height: totalHeight }}
    >
      {visibleRows}
      {showInsert && (
        // Fuera del rango virtualizado a propósito: es un único bloque, no una
        // colección de filas de ítems — no necesita windowing por scroll.
        <div ref={measureInsert} style={{ position: 'absolute', top: insertTop, left: 0, right: 0 }}>
          {insertContent}
        </div>
      )}
    </div>
  )
}
